import type { Database } from './database.types';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

type PublicTables = Database['public']['Tables'];
type EmailImportRowDb = PublicTables['email_imports']['Row'];
type EmailImportPaperRowDb = PublicTables['email_import_papers']['Row'];
type ProjectRowDb = PublicTables['project']['Row'];
type PaperRowDb = PublicTables['paper']['Row'];

export type EmailImportRow = Pick<
    EmailImportRowDb,
    | 'id'
    | 'created_at'
    | 'subject'
    | 'original_sender_email'
    | 'parse_state'
    | 'resolution_state'
    | 'parsed_paper_count'
> & {
    parser_key?: string | null;
    native_source_name?: string | null;
    native_source_label?: string | null;
    native_source_archive?: string | null;
    sender_label?: string | null;
    visible_candidate_count?: number;
};

export type EmailImportPaperResolvedPaper = Pick<
    PaperRowDb,
    'title' | 'doi' | 'url' | 'source_id' | 'source_paper_id'
>;

export type EmailImportPaperRoutedProject = Pick<
    ProjectRowDb,
    'is_inbox'
>;

export type EmailImportPaperRow = Pick<
    EmailImportPaperRowDb,
    | 'id'
    | 'email_import_id'
    | 'raw_title'
    | 'raw_url'
    | 'canonical_url'
    | 'status'
    | 'routing_state'
    | 'resolver_state'
    | 'link_kind'
    | 'access_state'
    | 'error_code'
    | 'paper_resolution_key'
    | 'paper_id'
    | 'project_paper_id'
> & {
    paper: EmailImportPaperResolvedPaper | null;
    routed_project: EmailImportPaperRoutedProject | null;
    matchedTopics?: MatchedTopicRef[];
};

export type MatchedTopicRef = {
    id: string;
    name: string;
};

const SUBJECT_PREFIX_PATTERN = /^(?:\s*(?:fwd?|fw|re)\s*:\s*)+/i;
const LONG_IMPORT_SUBJECT_CHARACTER_THRESHOLD = 64;

// i18n-deferred-start: taxonomy -- external source and publisher brand names.
const FRIENDLY_SOURCE_LABELS: Record<string, string> = {
    'scholar.google.com': 'Google Scholar',
    'google.com': 'Google Scholar',
    'arxiv.org': 'arXiv',
    'nature.com': 'Nature',
    'importai.net': 'Import AI',
    'substack.com': 'Substack',
    'storkapp.me': 'Stork',
};

const FRIENDLY_PARSER_LABELS: Record<string, string> = {
    arxiv_native_alert: 'arXiv',
    biorxiv_native_alert: 'bioRxiv',
    google_scholar: 'Google Scholar',
    huggingface_daily_papers: 'Hugging Face',
    medrxiv_native_alert: 'medRxiv',
    semantic_scholar_alert: 'Semantic Scholar',
    stork_alert: 'Stork',
};
// i18n-deferred-end

const NATIVE_SOURCE_ALERT_PARSER_KEYS = new Set([
    'arxiv_native_alert',
    'biorxiv_native_alert',
    'medrxiv_native_alert',
]);

const FRIENDLY_ERROR_COPY: Array<{
    match: (candidate: EmailImportPaperRow) => boolean;
    label: MessageDescriptor;
}> = [
    {
        match: (candidate) => ['held', 'failed'].includes(candidate.resolver_state || '') && Boolean(candidate.error_code),
        label: msg({ id: 'inbox.unresolved.insufficientDetails', message: 'We couldn’t extract enough paper details to add this automatically.' }),
    },
    {
        match: (candidate) => candidate.access_state === 'blocked',
        label: msg({ id: 'inbox.unresolved.couldNotOpen', message: 'We found a link but couldn’t open it' }),
    },
    {
        match: (candidate) => candidate.link_kind === 'paper_mention',
        label: msg({ id: 'inbox.unresolved.paperMention', message: 'This looked like a paper mention, but we couldn’t match it' }),
    },
    {
        match: (candidate) => Boolean(candidate.raw_url),
        label: msg({ id: 'inbox.unresolved.notAPaper', message: 'Couldn’t identify this link as a paper' }),
    },
];

function toTitleCase(value: string): string {
    return value
        .split(/[\s-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
}

export function cleanImportSubject(subject: string | null | undefined): string {
    const normalized = (subject || '').replace(SUBJECT_PREFIX_PATTERN, '').trim();
    return normalized || 'Untitled import';
}

export function importSubjectDisplay(subject: string | null | undefined): {
    primaryTitle: string;
    fullTitle: string;
    shouldShowFullTitle: boolean;
} {
    const fullTitle = cleanImportSubject(subject).replace(/\s+/g, ' ').trim();

    return {
        primaryTitle: fullTitle,
        fullTitle,
        shouldShowFullTitle: fullTitle.length > LONG_IMPORT_SUBJECT_CHARACTER_THRESHOLD,
    };
}

function senderLabelFromSubject(subject: string | null | undefined): string | null {
    const cleanedSubject = cleanImportSubject(subject).toLowerCase();
    if (cleanedSubject.includes('stork has brought you')) {
        // i18n-ignore: user-content -- external service brand.
        return 'Stork';
    }
    if (
        cleanedSubject.includes('semantic scholar')
        || cleanedSubject.includes("'influential papers' feed")
        || cleanedSubject.includes('influential papers feed')
    ) {
        // i18n-ignore: user-content -- external service brand.
        return 'Semantic Scholar';
    }
    return null;
}

function senderLabelFromParserKey(parserKey: string | null | undefined): string | null {
    const normalizedParserKey = String(parserKey || '').trim().toLowerCase();
    return FRIENDLY_PARSER_LABELS[normalizedParserKey] || null;
}

export function isNativeSourceAlertImport(
    importRow: Pick<EmailImportRow, 'parser_key'>
): boolean {
    return NATIVE_SOURCE_ALERT_PARSER_KEYS.has(String(importRow.parser_key || '').trim().toLowerCase());
}

function arxivArchiveFromSubject(subject: string | null | undefined): string | null {
    const match = cleanImportSubject(subject).match(/^([a-z0-9][a-z0-9.-]*)\s+daily\b/i);
    return match?.[1] || null;
}

export function nativeSourceArchiveForImport(
    importRow: Pick<EmailImportRow, 'native_source_archive' | 'subject'>
): string | null {
    return String(importRow.native_source_archive || '').trim() || arxivArchiveFromSubject(importRow.subject);
}

export function nativeSourceBaseLabelForImport(
    importRow: Pick<EmailImportRow, 'parser_key' | 'native_source_label' | 'native_source_name'>
): string | null {
    if (!isNativeSourceAlertImport(importRow)) {
        return null;
    }

    return String(importRow.native_source_label || '').trim()
        || senderLabelFromParserKey(importRow.parser_key)
        || toTitleCase(String(importRow.native_source_name || 'source'));
}

export function nativeSourceLabelForImport(
    importRow: Pick<EmailImportRow, 'parser_key' | 'native_source_archive' | 'native_source_label' | 'native_source_name' | 'subject'>
): string | null {
    const sourceLabel = nativeSourceBaseLabelForImport(importRow);
    if (!sourceLabel) {
        return null;
    }

    const archive = nativeSourceArchiveForImport(importRow);

    return archive ? `${archive} ${sourceLabel}` : sourceLabel;
}

export function senderLabelForImport(
    importRow: Pick<EmailImportRow, 'original_sender_email' | 'parser_key' | 'native_source_archive' | 'native_source_label' | 'native_source_name' | 'sender_label' | 'subject'>
): string {
    return String(importRow.sender_label || '').trim()
        || nativeSourceLabelForImport(importRow)
        || senderLabelFromParserKey(importRow.parser_key)
        || senderLabelFromSubject(importRow.subject)
        || senderLabelFromEmail(importRow.original_sender_email, importRow.subject);
}

export function senderLabelFromEmail(
    email: string | null | undefined,
    subject?: string | null | undefined
): string {
    if (!email) {
        return senderLabelFromSubject(subject) || UNKNOWN_SENDER_LABEL;
    }

    const domain = email.split('@')[1]?.toLowerCase() || email.toLowerCase();

    if (FRIENDLY_SOURCE_LABELS[domain]) {
        return FRIENDLY_SOURCE_LABELS[domain];
    }

    const rootDomain = domain.split('.').slice(-2).join('.');
    if (FRIENDLY_SOURCE_LABELS[rootDomain]) {
        return FRIENDLY_SOURCE_LABELS[rootDomain];
    }

    return toTitleCase(rootDomain.replace(/\.[a-z]+$/i, '').replace(/[._]/g, ' '));
}

// i18n-ignore: technical-token -- internal sentinel resolved at presentation boundaries.
export const UNKNOWN_SENDER_LABEL = 'paperzilla:unknown-sender';

export function unresolvedReasonLabel(candidate: EmailImportPaperRow): MessageDescriptor {
    const match = FRIENDLY_ERROR_COPY.find((entry) => entry.match(candidate));
    return match?.label || msg({ id: 'inbox.unresolved.notAPaper', message: 'Couldn’t identify this link as a paper' });
}

export function isHiddenRejectedControlCandidate(
    candidate: Pick<EmailImportPaperRow, 'status' | 'resolver_state' | 'link_kind'>
): boolean {
    return candidate.status === 'rejected'
        || candidate.resolver_state === 'rejected'
        || candidate.link_kind === 'control';
}
